import FormikCheckBox from '@boilerplate/components/FormikCheckBox';
import FormikTextField from '@boilerplate/components/FormikTextField';
import PasswordStrengthIndicator from '@boilerplate/components/auth/PasswordStrengthIndicator';
import { Button } from '@mui/material';
import axios, { AxiosError } from 'axios';
import { Form, Formik, FormikErrors, FormikHelpers } from 'formik';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import isEmail from 'validator/es/lib/isEmail';

import config from '@/config';
import Notistack from '@/lib/notistack';
import { useTenantInfo, ErrorMessage } from '@/lib/oauth';
import { passwordStrength } from '@/lib/passwordStrength';
import PageLoading from '@/routes/PageLoading';

import OAuthLayout from './Layout/OAuthLayout';

interface FormValues {
  email: string;
  password: string;
  passwordConfirmation: string;
  termsAndConditions?: boolean;
}

const initialValues: FormValues = {
  email: '',
  password: '',
  passwordConfirmation: '',
  termsAndConditions: false,
};

function OAuthRegisterPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { tenantInfo, tenantInfoLoading } = useTenantInfo(searchParams.get('client_id') ?? '');
  const nav = useNavigate();

  const validate = (values: FormValues) => {
    const errors: FormikErrors<FormValues> = {};

    if (tenantInfo?.termsAndConditions && !values.termsAndConditions) {
      errors.termsAndConditions = t('auth:validation.required');
    }

    if (!values.email) {
      errors.email = t('auth:validation.required');
    } else if (!isEmail(values.email)) {
      errors.email = t('auth:validation.emailInvalid');
    }

    if (!values.password) {
      errors.password = t('auth:validation.required');
    } else if (passwordStrength(values.password) < config.auth.passwordStrength) {
      errors.password = t('auth:validation.passwordTooWeak');
    } else if (values.password !== values.passwordConfirmation) {
      errors.passwordConfirmation = t('auth:validation.passwordConfirmationMustMatch');
    }

    return errors;
  };

  const handleSubmit = (values: FormValues, { setSubmitting, setFieldError, resetForm }: FormikHelpers<FormValues>) => {
    const registerRequest = {
      email: values.email,
      password: values.password,
      client_id: searchParams.get('client_id'),
      termsAndConditions: values.termsAndConditions,
    };

    axios
      .post('/oauth/register', registerRequest)
      .then(() => {
        resetForm();
        Notistack.toast(t(`auth:register.success.general`), {
          variant: 'success',
        });
        nav(`/oauth/authorize?${searchParams.toString()}`);
      })
      .catch((error: AxiosError) => {
        if (error?.response?.status === 409) {
          setFieldError('email', t('auth:validation.emailAlreadyRegistered'));
        } else {
          Notistack.toast(t('auth:register.error.unknown'), { variant: 'error' });
        }
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  if (tenantInfoLoading) {
    return <PageLoading />;
  }

  if (!tenantInfo) {
    return <ErrorMessage message={t('oauth:error.invalidClientId')} />;
  }

  if (tenantInfo.redirectUri !== searchParams.get('redirect_uri')) {
    return <ErrorMessage message={t('oauth:error.invalidRedirectUri')} />;
  }

  if (!tenantInfo.registerEnabled) {
    nav(`/oauth/authorize?${searchParams.toString()}`);
  }

  return (
    <OAuthLayout heading={t('auth:register.title')} tenantName={tenantInfo?.name} maxWidth="xs">
      <Formik initialValues={initialValues} validate={validate} onSubmit={handleSubmit}>
        {({ isSubmitting, values }) => (
          <Form>
            <FormikTextField type="email" name="email" label={t('auth:fields.email')} />

            <FormikTextField type="password" name="password" label={t('auth:fields.password')} />

            <PasswordStrengthIndicator password={values.password} />

            <FormikTextField type="password" name="passwordConfirmation" label={t('auth:fields.passwordConfirmation')} />

            {tenantInfo?.termsAndConditions && (
              <FormikCheckBox
                name="termsAndConditions"
                label={
                  <Trans
                    i18nKey="auth:fields.termsAndConditions"
                    components={{
                      a: <a href={tenantInfo?.termsAndConditions} target="_blank" rel="noreferrer" />,
                    }}
                  />
                }
              />
            )}

            <Button type="submit" disabled={isSubmitting} size="large" variant="contained" color="primary" sx={{ marginTop: '1rem' }} fullWidth>
              {t('auth:register.submit')}
            </Button>
          </Form>
        )}
      </Formik>

      <p>
        <Link to={`/oauth/authorize?${searchParams.toString()}`}>{t('auth:links.alreadyHaveAnAccountLogin')}</Link>
      </p>
    </OAuthLayout>
  );
}

export default OAuthRegisterPage;
